import React, { useEffect, useRef, useState } from "react";
import {
  TABLE_WIDTH,
  TABLE_HEIGHT,
  WALLS,
  BUMPERS,
  TARGETS,
  LEFT_FLIPPER,
  RIGHT_FLIPPER,
  LAUNCH_POSITION,
  LAUNCH_VELOCITY,
  BALL_RADIUS,
  GRAVITY,
  WALL_RESTITUTION,
  BUMPER_KICK,
  FLIPPER_KICK,
  FLIPPER_WIDTH,
  FLIPPER_REST_RESTITUTION,
  STALL_SPEED_THRESHOLD,
  STALL_TIMEOUT_MS,
  STALL_ZONE_Y,
  TARGET_POINTS,
  BUMPER_POINTS,
  JACKPOT_BONUS,
  wallNormal,
  closestPointOnSegment,
  reflect,
  circleCircleCollision,
  resolveBumperHit,
  isBallInDrain,
} from "./data.js";
import { playSound } from "../../lib/sound.js";

const FLASH_MS = 150;

function flipperTip(flipper, angle) {
  return {
    x: flipper.pivot.x + Math.cos(angle) * flipper.length,
    y: flipper.pivot.y + Math.sin(angle) * flipper.length,
  };
}

function drawTable(ctx, { litTargetIds, flashUntil, leftAngle, rightAngle, ball, ballVisible }) {
  const now = performance.now();

  ctx.clearRect(0, 0, TABLE_WIDTH, TABLE_HEIGHT);
  ctx.fillStyle = "#0D0221";
  ctx.fillRect(0, 0, TABLE_WIDTH, TABLE_HEIGHT);

  ctx.strokeStyle = "#B9AEDC";
  ctx.lineWidth = 3;
  ctx.beginPath();
  for (const wall of WALLS) {
    ctx.moveTo(wall.x1, wall.y1);
    ctx.lineTo(wall.x2, wall.y2);
  }
  ctx.stroke();

  for (const bumper of BUMPERS) {
    const flashing = (flashUntil[bumper.id] || 0) > now;
    ctx.beginPath();
    ctx.arc(bumper.x, bumper.y, flashing ? bumper.radius * 1.15 : bumper.radius, 0, Math.PI * 2);
    ctx.fillStyle = flashing ? "#FFFFFF" : "#FF2E8A";
    ctx.fill();
  }

  for (const target of TARGETS) {
    ctx.beginPath();
    ctx.arc(target.x, target.y, target.radius, 0, Math.PI * 2);
    ctx.fillStyle = litTargetIds.has(target.id) ? "#39FF14" : "#00F0FF";
    ctx.fill();
  }

  ctx.strokeStyle = "#FFD400";
  ctx.lineWidth = FLIPPER_WIDTH;
  ctx.lineCap = "round";
  for (const [flipper, angle] of [
    [LEFT_FLIPPER, leftAngle],
    [RIGHT_FLIPPER, rightAngle],
  ]) {
    const tip = flipperTip(flipper, angle);
    ctx.beginPath();
    ctx.moveTo(flipper.pivot.x, flipper.pivot.y);
    ctx.lineTo(tip.x, tip.y);
    ctx.stroke();
  }

  if (ballVisible) {
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, BALL_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = "#FFFFFF";
    ctx.fill();
  }

  if (!ballVisible) return;
  const inLane = ball.x > 355 && ball.vx === 0 && ball.vy === 0;
  if (inLane) {
    ctx.fillStyle = "#B9AEDC";
    ctx.font = "10px 'Press Start 2P', monospace";
    ctx.textAlign = "right";
    ctx.fillText("TAP TO LAUNCH", ball.x - BALL_RADIUS - 10, ball.y);
  }
}

export default function PinballTable({ onScoreDelta, onBallLost, onJackpot, onGameOver }) {
  const canvasRef = useRef(null);
  const ballRef = useRef({ x: LAUNCH_POSITION.x, y: LAUNCH_POSITION.y, vx: 0, vy: 0 });
  const leftAngleRef = useRef(LEFT_FLIPPER.restAngle);
  const rightAngleRef = useRef(RIGHT_FLIPPER.restAngle);
  const leftActiveRef = useRef(false);
  const rightActiveRef = useRef(false);
  const litTargetsRef = useRef(new Set());
  const flashUntilRef = useRef({});
  const lastTimeRef = useRef(null);
  const rafRef = useRef(null);
  const ballsLostRef = useRef(0);
  const hasLaunchedRef = useRef(false);
  const stallStartRef = useRef(null);
  const [ballVisible, setBallVisible] = useState(true);

  const launchBall = () => {
    const b = ballRef.current;
    if (b.vx !== 0 || b.vy !== 0) return; // already launched
    b.vx = LAUNCH_VELOCITY.x;
    b.vy = LAUNCH_VELOCITY.y;
    playSound("coin");
    hasLaunchedRef.current = true;
  };

  const respawnBall = () => {
    ballRef.current = { x: LAUNCH_POSITION.x, y: LAUNCH_POSITION.y, vx: 0, vy: 0 };
    setBallVisible(true);
    hasLaunchedRef.current = false;
    stallStartRef.current = null;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = TABLE_WIDTH * dpr;
    canvas.height = TABLE_HEIGHT * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    const handleKeyDown = (e) => {
      if (e.code === "ArrowLeft" || e.code === "KeyA") leftActiveRef.current = true;
      if (e.code === "ArrowRight" || e.code === "KeyD") rightActiveRef.current = true;
      if (e.code === "Space") launchBall();
    };
    const handleKeyUp = (e) => {
      if (e.code === "ArrowLeft" || e.code === "KeyA") leftActiveRef.current = false;
      if (e.code === "ArrowRight" || e.code === "KeyD") rightActiveRef.current = false;
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    const handlePointerDown = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      launchBall();
      if (x < rect.width / 2) leftActiveRef.current = true;
      else rightActiveRef.current = true;
    };
    const handlePointerUp = () => {
      leftActiveRef.current = false;
      rightActiveRef.current = false;
    };
    canvas.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("pointerup", handlePointerUp);

    const step = (now) => {
      if (lastTimeRef.current === null) lastTimeRef.current = now;
      const dt = Math.min(0.032, (now - lastTimeRef.current) / 1000);
      lastTimeRef.current = now;

      const b = ballRef.current;

      // Flipper angles ease toward their target (active vs. rest) angle.
      for (const [ref, flipper, active] of [
        [leftAngleRef, LEFT_FLIPPER, leftActiveRef.current],
        [rightAngleRef, RIGHT_FLIPPER, rightActiveRef.current],
      ]) {
        const target = active ? flipper.activeAngle : flipper.restAngle;
        const diff = target - ref.current;
        const maxStep = flipper.angularSpeed * dt;
        ref.current += Math.max(-maxStep, Math.min(maxStep, diff));
      }

      // Advance physics in several smaller substeps rather than one big
      // step. At full speed the ball can move well past the width of a
      // flipper (or even a wall) in a single ~16ms frame, letting it tunnel
      // through a collision zone without ever landing inside it on any
      // sampled frame — most noticeably right at the flippers, where a
      // fast ball could pass clean through into the drain, "under" them,
      // without ever registering a bounce. Substepping shrinks the
      // per-check travel distance so collisions are caught reliably.
      const SUBSTEPS = 4;
      const subDt = dt / SUBSTEPS;
      for (let sub = 0; sub < SUBSTEPS; sub += 1) {
        if (b.vx === 0 && b.vy === 0) break;

        b.vy += GRAVITY * subDt;
        b.x += b.vx * subDt;
        b.y += b.vy * subDt;

        // Walls
        for (const wall of WALLS) {
          const closest = closestPointOnSegment(b, { x: wall.x1, y: wall.y1 }, { x: wall.x2, y: wall.y2 });
          const dx = b.x - closest.x;
          const dy = b.y - closest.y;
          const dist = Math.hypot(dx, dy);
          if (dist < BALL_RADIUS) {
            const normal = wallNormal(wall);
            const bounced = reflect({ x: b.vx, y: b.vy }, normal, WALL_RESTITUTION);
            b.vx = bounced.x;
            b.vy = bounced.y;
            const overlap = BALL_RADIUS - dist;
            b.x += normal.x * overlap;
            b.y += normal.y * overlap;
          }
        }

        // Bumpers
        for (const bumper of BUMPERS) {
          const hit = circleCircleCollision(b, BALL_RADIUS, bumper, bumper.radius);
          if (hit) {
            const bounced = resolveBumperHit(b, { x: b.vx, y: b.vy }, bumper, BUMPER_KICK);
            b.vx = bounced.x;
            b.vy = bounced.y;
            b.x += (hit.dx / hit.distance) * hit.overlap;
            b.y += (hit.dy / hit.distance) * hit.overlap;
            flashUntilRef.current[bumper.id] = performance.now() + FLASH_MS;
            onScoreDelta(BUMPER_POINTS);
            playSound("coin");
          }
        }

        // Targets
        for (const target of TARGETS) {
          if (litTargetsRef.current.has(target.id)) continue;
          const hit = circleCircleCollision(b, BALL_RADIUS, target, target.radius);
          if (hit) {
            litTargetsRef.current.add(target.id);
            onScoreDelta(TARGET_POINTS);
            playSound("coin");
            if (litTargetsRef.current.size === TARGETS.length) {
              litTargetsRef.current = new Set();
              onScoreDelta(JACKPOT_BONUS);
              onJackpot();
              playSound("challenge");
            }
          }
        }

        // Flippers (collision point treated like a small bumper; kick only
        // applies while the flipper is actively being raised)
        for (const [flipper, angleRef, active] of [
          [LEFT_FLIPPER, leftAngleRef, leftActiveRef.current],
          [RIGHT_FLIPPER, rightAngleRef, rightActiveRef.current],
        ]) {
          const tip = flipperTip(flipper, angleRef.current);
          const closest = closestPointOnSegment(b, flipper.pivot, tip);
          const dist = Math.hypot(b.x - closest.x, b.y - closest.y);
          if (dist < BALL_RADIUS + FLIPPER_WIDTH / 2) {
            const bounced = resolveBumperHit(
              b,
              { x: b.vx, y: b.vy },
              closest,
              active ? FLIPPER_KICK : 0,
              active ? 1 : FLIPPER_REST_RESTITUTION
            );
            b.vx = bounced.x;
            b.vy = bounced.y;
          }
        }

        // Drain
        if (isBallInDrain(b)) {
          b.vx = 0;
          b.vy = 0;
          playSound("error");
          onBallLost();
          ballsLostRef.current += 1;
          if (ballsLostRef.current >= 3) {
            setBallVisible(false);
            onGameOver();
          } else {
            respawnBall();
          }
          break;
        }
      }

      // Stall failsafe: a rare resting-contact equilibrium (e.g. a
      // near-elastic bounce right at a flipper's collision boundary) could
      // otherwise let the ball sit there indefinitely without ever
      // reaching the drain. If it's launched, near the bottom of the
      // table, and essentially motionless for a sustained stretch, treat
      // it as lost the same way draining does.
      if (hasLaunchedRef.current && b.y > STALL_ZONE_Y && Math.hypot(b.vx, b.vy) < STALL_SPEED_THRESHOLD) {
        if (!stallStartRef.current) {
          stallStartRef.current = now;
        } else if (now - stallStartRef.current >= STALL_TIMEOUT_MS) {
          b.vx = 0;
          b.vy = 0;
          playSound("error");
          onBallLost();
          ballsLostRef.current += 1;
          if (ballsLostRef.current >= 3) {
            setBallVisible(false);
            onGameOver();
          } else {
            respawnBall();
          }
        }
      } else {
        stallStartRef.current = null;
      }

      const ctx2 = canvasRef.current.getContext("2d");
      drawTable(ctx2, {
        litTargetIds: litTargetsRef.current,
        flashUntil: flashUntilRef.current,
        leftAngle: leftAngleRef.current,
        rightAngle: rightAngleRef.current,
        ball: ballRef.current,
        ballVisible,
      });

      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      canvas.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("pointerup", handlePointerUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ballVisible]);

  return (
    <canvas
      ref={canvasRef}
      className="cc-table"
      style={{
        width: TABLE_WIDTH,
        maxWidth: "100%",
        height: "auto",
        aspectRatio: `${TABLE_WIDTH} / ${TABLE_HEIGHT}`,
      }}
    />
  );
}
